/**
 * The Museum's history: one plaque per era ended.
 *
 * This is where the Era Log lives — it used to be a run-on line each on the
 * Stats page, which had no room for the dinosaur that era is remembered by.
 * A museum is the right home for it, and excavated carries nothing but the
 * memory now, so the memory is what this shows.
 */

import { DINOS_BY_ID } from '../data/dinos';
import type { DinoDef } from '../data/dinos';
import { ERA_MODIFIERS_BY_ID, EXPEDITIONS_BY_ID } from '../data/eras';
import { formatDuration, formatEggs } from '../game/numbers';
import { dinoSpriteUrl } from './sprites';
import type { EraRecord, GameState } from '../game/state';

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

/**
 * What to call the dinosaur excavated at an era's end. Records written before the name and
 * the sprite were captured have only the tier's id, so those fall back to the
 * name book — right in every case but a rename between eras.
 */
function excavatedOf(
  row: EraRecord,
  state: GameState,
): { def: DinoDef; sprite: string; name: string | null; species: string } | null {
  if (row.excavated === null) return null;
  const def = DINOS_BY_ID.get(row.excavated);
  if (!def) return null;
  const sprite = row.excavatedSprite ?? def.id;
  const species = sprite === def.id ? def.name : def.evolved.name;
  return { def, sprite, name: row.excavatedName ?? state.nameBook[def.id] ?? null, species };
}

/** The glass case, with the dinosaur in it or visibly without one. */
function caseFor(kept: ReturnType<typeof excavatedOf>): HTMLElement {
  const box = el('span', 'era__case');
  if (!kept) {
    box.classList.add('era__case--empty');
    box.textContent = 'none';
    return box;
  }
  const img = new Image();
  img.className = 'era__sprite';
  img.alt = '';
  // The dinosaur as it lived, not its bones: the Collection shows the
  // skeletons, and a plaque remembers the animal that era was about.
  img.src = dinoSpriteUrl(kept.sprite, 'full');
  box.append(img);
  return box;
}

function plaque(row: EraRecord, state: GameState): HTMLElement {
  const kept = excavatedOf(row, state);
  const node = el('div', 'era');

  const body = el('div', 'era__body');
  const top = el('div', 'era__top');
  top.append(
    el('span', 'era__number', `Era ${row.era}`),
    el(
      'span',
      'era__mod',
      row.eraId === null ? 'Plain' : (ERA_MODIFIERS_BY_ID.get(row.eraId)?.name ?? row.eraId),
    ),
  );

  const who = el(
    'div',
    'era__who',
    kept === null
      ? 'Left in the ground'
      : kept.name === null
        ? `A ${kept.species}`
        : `"${kept.name}" the ${kept.species}`,
  );
  if (kept === null) who.classList.add('era__who--none');

  // Figure first, label after: the number is what is being compared down the
  // page and the word only says which number it is.
  const facts = el('div', 'era__facts');
  const fact = (value: string, label: string): void => {
    const span = el('span', 'era__fact');
    span.append(el('b', '', value), label === '' ? '' : ` ${label}`);
    facts.append(span);
  };
  fact(formatEggs(row.eggs), 'eggs');
  fact(String(row.fossils), row.fossils === 1 ? 'fossil' : 'fossils');
  fact(String(row.tiers), row.tiers === 1 ? 'tier' : 'tiers');
  fact(formatDuration(row.seconds), '');
  if (row.expedition !== null) {
    facts.append(
      el('span', 'era__trip', EXPEDITIONS_BY_ID.get(row.expedition)?.name ?? row.expedition),
    );
  }

  body.append(top, who, facts);
  node.append(caseFor(kept), body);
  return node;
}

export interface HistoryView {
  /** The element to put in the Museum, heading included. */
  readonly element: HTMLElement;
  update(state: GameState): void;
}

export function createHistoryView(): HistoryView {
  const wrap = el('div', 'history');
  const summary = el('p', 'history__summary');
  const list = el('div', 'history__list');
  wrap.append(summary, list);

  let lastLog: readonly EraRecord[] | null = null;
  let lastSummary = '';

  return {
    element: wrap,
    update(state) {
      const eras = state.eraLog.length;
      const raised = state.fossilsEarned;
      const laid = state.eraLog.reduce((sum, r) => sum + r.eggs, 0);
      const text =
        eras === 0
          ? ''
          : `${eras} era${eras === 1 ? '' : 's'} ended · ${formatEggs(raised)} fossil${raised === 1 ? '' : 's'} raised · ${formatEggs(laid)} eggs laid in all`;
      if (text !== lastSummary) {
        lastSummary = text;
        summary.textContent = text;
        summary.hidden = text === '';
      }

      // Newest first. The log is replaced, never edited, on every extinction,
      // import and wipe, so a new array is the only sign of change needed.
      // (The length was watched before, and stopped changing when the log was capped:
      // past fifty eras, a new plaque never appeared.)
      if (state.eraLog !== lastLog) {
        lastLog = state.eraLog;
        list.replaceChildren(...[...state.eraLog].reverse().map((row) => plaque(row, state)));
      }
    },
  };
}
